import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  PlusIcon,
  MinusIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import AsyncSelect from '@/components/ui/async-select';
import SingleSelect, {
  SingleSelectOption,
} from '@/components/ui/single-select';
import {
  getSlateReportMoviesSearch,
  GetSlateReportMoviesSearch,
  getSlateReportPredictions,
  GetSlateReportPredictions,
  postSlateReport,
  PostSlateReport,
} from '../../../api/slate-report';
import MovieItem from './MovieItem';
import PredictionItem from './PredictionItem';
import SortFilter from './SortFilter';

interface SlateReportModalProps {
  onOpenChange: () => void;
}

interface ReportItem {
  movie: GetSlateReportMoviesSearch.Movie | null;
  prediction: GetSlateReportPredictions.Prediction | null;
  predictions: GetSlateReportPredictions.Prediction[];
  loadingPredictions: boolean;
  sortBy: GetSlateReportPredictions.SortBy;
}

const SlateReportModal: React.FC<SlateReportModalProps> = ({
  onOpenChange,
}) => {
  const history = useHistory();
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const [reportItems, setReportItems] = useState<ReportItem[]>([
    {
      movie: null,
      prediction: null,
      predictions: [],
      loadingPredictions: false,
      sortBy: 'date',
    },
    {
      movie: null,
      prediction: null,
      predictions: [],
      loadingPredictions: false,
      sortBy: 'date',
    },
  ]);
  const [isCreatingReport, setIsCreatingReport] = useState(false);

  const loadMovies = async (
    query: string,
  ): Promise<GetSlateReportMoviesSearch.Movie[]> => {
    const result = await getSlateReportMoviesSearch({
      title: query,
      limit: 20,
      offset: 0,
    });

    if ('data' in result) {
      return result.data.results;
    }

    console.error('Failed to load movies:', result.error);
    return [];
  };

  const loadPredictionsForItem = async (
    itemIndex: number,
    movie: GetSlateReportMoviesSearch.Movie,
    customSortBy?: GetSlateReportPredictions.SortBy,
    preserveSelection = false,
  ) => {
    setReportItems((prev) =>
      prev.map((item, index) =>
        index === itemIndex
          ? {
              ...item,
              loadingPredictions: true,
              predictions: [],
              prediction: preserveSelection ? item.prediction : null,
            }
          : item,
      ),
    );

    const sortByValue =
      customSortBy || reportItems[itemIndex]?.sortBy || 'date';
    const result = await getSlateReportPredictions(movie.id, {
      sort_by: sortByValue,
    });

    if ('data' in result) {
      setReportItems((prev) =>
        prev.map((item, index) =>
          index === itemIndex
            ? { ...item, predictions: result.data, loadingPredictions: false }
            : item,
        ),
      );
    } else {
      console.error('Failed to load predictions:', result.error);
      setReportItems((prev) =>
        prev.map((item, index) =>
          index === itemIndex
            ? { ...item, predictions: [], loadingPredictions: false }
            : item,
        ),
      );
    }
  };

  const getPredictionOptions = (
    predictions: GetSlateReportPredictions.Prediction[],
  ) =>
    predictions.map((prediction) => ({
      value: prediction.id,
      label: prediction.version,
      prediction: prediction,
    }));

  const renderPredictionItem = (
    option: SingleSelectOption<string> & {
      prediction?: GetSlateReportPredictions.Prediction;
    },
    isSelected: boolean,
    onClick: () => void,
  ) => (
    <PredictionItem option={option} isSelected={isSelected} onClick={onClick} />
  );

  const renderSelectedPrediction = (
    option: SingleSelectOption<string> & {
      prediction?: GetSlateReportPredictions.Prediction;
    },
  ) => {
    const prediction = option.prediction;
    if (!prediction) return option.label;

    return (
      <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-min-w-0 tw-overflow-hidden">
        <span className="tw-font-semibold tw-text-gray-900 tw-text-sm tw-truncate tw-flex-shrink-0 tw-max-w-[30%]">
          {prediction.version}
        </span>
        <span className="tw-px-2 tw-py-0.5 tw-bg-blue-100 tw-text-blue-800 tw-text-xs tw-font-medium tw-rounded-full tw-shrink-0">
          {new Date(prediction.date).toLocaleDateString()}
        </span>
        <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 tw-shrink-0">
          <span className="tw-font-medium tw-text-green-700">
            ${(prediction.box_office_gross / 1000000).toFixed(1)}M
          </span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 tw-shrink-0">
          <span className="tw-font-medium tw-text-purple-700">
            {prediction.box_office_multiple.toFixed(1)}x
          </span>
        </div>
      </div>
    );
  };

  const handleMovieChange = (
    itemIndex: number,
    movie: GetSlateReportMoviesSearch.Movie | null,
  ) => {
    setReportItems((prev) =>
      prev.map((item, index) =>
        index === itemIndex
          ? { ...item, movie, prediction: null, predictions: [] }
          : item,
      ),
    );

    if (movie) {
      loadPredictionsForItem(itemIndex, movie);
    }
  };

  const handlePredictionChange = (
    itemIndex: number,
    predictionId: string | null,
  ) => {
    setReportItems((prev) =>
      prev.map((item, index) => {
        if (index === itemIndex) {
          const prediction = predictionId
            ? item.predictions.find((p) => p.id === predictionId) || null
            : null;
          return { ...item, prediction };
        }
        return item;
      }),
    );
  };

  const handleSortByChange = (
    itemIndex: number,
    newSortBy: GetSlateReportPredictions.SortBy,
  ) => {
    setReportItems((prev) =>
      prev.map((item, index) =>
        index === itemIndex ? { ...item, sortBy: newSortBy } : item,
      ),
    );

    const item = reportItems[itemIndex];
    if (item.movie) {
      loadPredictionsForItem(itemIndex, item.movie, newSortBy, true);
    }
  };

  const handlePredictionDropdownOpen = (itemIndex: number) => {
    const item = reportItems[itemIndex];
    if (item.movie && !item.loadingPredictions) {
      loadPredictionsForItem(itemIndex, item.movie, undefined, true);
    }
  };

  const addReportItem = () => {
    const newItem: ReportItem = {
      movie: null,
      prediction: null,
      predictions: [],
      loadingPredictions: false,
      sortBy: 'date',
    };
    setReportItems((prev) => [...prev, newItem]);
  };

  const removeReportItem = (itemIndex: number) => {
    if (reportItems.length > 1) {
      setReportItems((prev) => prev.filter((_, index) => index !== itemIndex));
    }
  };

  const renderMovieItem = (
    movie: GetSlateReportMoviesSearch.Movie,
    index: number,
    isHighlighted: boolean,
    onSelect: (movie: GetSlateReportMoviesSearch.Movie) => void,
  ) => {
    const isAlreadySelected = reportItems.some(
      (item) => item.movie?.id === movie.id,
    );

    if (isAlreadySelected) {
      return (
        <TooltipProvider key={index}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="tw-w-full">
                <MovieItem
                  movie={movie}
                  index={index}
                  isHighlighted={false}
                  onSelect={() => {}}
                  disabled={true}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>This film is already selected</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <MovieItem
        key={index}
        movie={movie}
        index={index}
        isHighlighted={isHighlighted}
        onSelect={onSelect}
      />
    );
  };

  const getValidItems = () => {
    return reportItems.filter((item) => item.movie && item.prediction);
  };

  const isValidForReport = () => {
    const validItems = getValidItems();
    return validItems.length >= 2;
  };

  const handleCreateReport = async () => {
    const validItems = getValidItems();

    if (validItems.length < 2) {
      alert(
        'Please select at least 2 movies with predictions to create a report.',
      );
      return;
    }

    const requestBody: PostSlateReport.RequestBody = {
      requested_data: validItems.map((item) => ({
        movie_id: item.movie!.id,
        prediction_id: item.prediction!.id,
      })),
    };

    setIsCreatingReport(true);
    try {
      const url = new URL('/report-print', window.location.origin);
      url.searchParams.append('source', 'slate-report');
      validItems.forEach((item, index) => {
        url.searchParams.append(`movieId_${index}`, item.movie!.id);
        url.searchParams.append(`predictionId_${index}`, item.prediction!.id);
      });
      url.searchParams.append('itemsCount', validItems.length.toString());
      window.open(url.toString(), '_blank', 'noopener,noreferrer');

      onOpenChange();
    } catch (error) {
      console.error('Error opening slate report:', error);
      alert('An error occurred while opening the slate report.');
    } finally {
      setIsCreatingReport(false);
    }
  };

  const handleNavigateToSavedReports = () => {
    history.push('/library#slate-reports');
    onOpenChange();
  };

  return (
    <Dialog defaultOpen modal={true} onOpenChange={onOpenChange}>
      <DialogContent
        ref={(node) => setPortalContainer(node)}
        className="tw-w-full !tw-max-w-[800px]"
      >
        <DialogHeader>
          <DialogTitle>Create Slate Report</DialogTitle>
        </DialogHeader>

        <div className="tw-py-4">
          <div className="tw-flex">
            <div className="tw-w-[40%]">
              <Label>Film</Label>
            </div>
            <div className="tw-w-[60%] tw-pl-6">
              <Label>Prediction Version</Label>
            </div>
          </div>

          <div className="tw-space-y-3">
            {reportItems.map((item, index) => (
              <div key={index} className="tw-flex">
                <div className="tw-w-[40%]">
                  <AsyncSelect<GetSlateReportMoviesSearch.Movie>
                    value={item.movie}
                    onChange={(movie) => handleMovieChange(index, movie)}
                    loadSuggestions={loadMovies}
                    placeholder="Search..."
                    renderItem={renderMovieItem}
                    getDisplayValue={(movie) => movie.title}
                    getSearchValue={(movie) => movie.title}
                    minChars={2}
                    debounceMs={300}
                    key={`movie-select-${index}`}
                  />
                </div>
                <div className="tw-w-[60%] tw-pl-6">
                  <div className="tw-flex tw-gap-2 tw-w-full">
                    <div className="tw-flex-1">
                      <SingleSelect
                        options={getPredictionOptions(item.predictions)}
                        value={item.prediction?.id}
                        onChange={(value) =>
                          handlePredictionChange(index, value || null)
                        }
                        placeholder={
                          !item.movie
                            ? 'Select a movie first'
                            : item.loadingPredictions
                              ? 'Loading predictions...'
                              : 'Select a prediction version...'
                        }
                        disabled={!item.movie || item.loadingPredictions}
                        searchable={true}
                        showClearButton={true}
                        renderItem={renderPredictionItem}
                        renderSelectedValue={renderSelectedPrediction}
                        searchPlaceholder="Search..."
                        emptyText={
                          item.loadingPredictions
                            ? 'Loading predictions...'
                            : 'No predictions found'
                        }
                        maxHeight="tw-max-h-[200px]"
                        onDropdownOpen={() =>
                          handlePredictionDropdownOpen(index)
                        }
                        headerContent={
                          <SortFilter
                            sortBy={item.sortBy}
                            onSortChange={(newSortBy) =>
                              handleSortByChange(index, newSortBy)
                            }
                          />
                        }
                        portalContainer={portalContainer}
                        key={`prediction-select-${index}`}
                      />
                    </div>
                    {index === reportItems.length - 1 ? (
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={addReportItem}
                      >
                        <PlusIcon className="tw-h-4 tw-w-4" />
                      </Button>
                    ) : (
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => removeReportItem(index)}
                        disabled={reportItems.length === 1}
                      >
                        <MinusIcon className="tw-h-4 tw-w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={handleNavigateToSavedReports}
                    className="tw-gap-2"
                  >
                    <ArchiveBoxIcon className="tw-h-4 tw-w-4" />
                    <span>Saved Reports</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>View saved slate reports</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <div className="tw-flex tw-justify-center">
              {!isCreatingReport && !isValidForReport() ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        onClick={handleCreateReport}
                        disabled={isCreatingReport || !isValidForReport()}
                      >
                        {isCreatingReport
                          ? 'Creating Report...'
                          : 'Create Report'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Please select at least 2 movies with prediction versions
                      to create a report
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button
                  onClick={handleCreateReport}
                  disabled={isCreatingReport || !isValidForReport()}
                >
                  {isCreatingReport ? 'Creating Report...' : 'Create Report'}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SlateReportModal;
